import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Card, Chip, IconButton, Typography } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { FormatUtils } from '@mega-pokedex/shared';
import { EnrichedOwnership } from '../../../hooks/useEnrichedCollection';

interface CollectionCardProps {
  card: EnrichedOwnership;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, cardId: string) => void;
}

const CollectionCard: React.FC<CollectionCardProps> = ({
  card,
  onMenuClick,
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    // Navigate to collection card detail page with setId and cardId, passing card data via state
    // Use setId from data if available (e.g. for Pokemon 'base1'), otherwise fallback to setName
    const setId = card.setId || card.setName;
    navigate(`/collection/${setId}/${card.id}`, {
      state: { card },
    });
  };

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // Prevent card click when clicking menu
    onMenuClick(e, card.id);
  };

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
      onClick={handleCardClick}
    >
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <IconButton
          size="small"
          onClick={handleMenuClick}
          sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
        >
          <MoreVert />
        </IconButton>
      </Box>

      {/* Compact Layout - Image and Info Side by Side */}
      <Box sx={{ display: 'flex', p: 1.5, gap: 1.5 }}>
        {/* Card Image */}
        <Box sx={{ flexShrink: 0 }}>
          <Avatar
            src={card.imageUrl}
            alt={card.cardName}
            sx={{
              width: 110,
              height: 154,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.200',
            }}
            variant="rounded"
          />
        </Box>

        {/* Card Info */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {/* Title and Set */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.25 }}
              noWrap
            >
              {card.justtcgName || card.cardName}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              noWrap
            >
              {card.setName} • #{card.number}
            </Typography>
          </Box>

          {/* Game Badge + Condition + Qty in one row */}
          <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
            <Chip
              label={card.game === 'pokemon' ? 'Pokémon' : 'One Piece'}
              size="small"
              color={card.game === 'pokemon' ? 'primary' : 'secondary'}
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'medium' }}
            />
            <Chip
              label={card.language === 'JP' ? 'Japanese' : 'English'}
              size="small"
              variant="outlined"
              color={card.language === 'JP' ? 'warning' : 'default'}
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
            <Chip
              label={card.condition.replace('_', ' ')}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
            <Chip
              label={`Qty: ${card.quantity}`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          </Box>

          {/* Rarity if available */}
          {card.rarity && (
            <Chip
              label={card.rarity}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem', alignSelf: 'flex-start' }}
            />
          )}

          {/* Price Info - Compact */}
          <Box display="flex" gap={2} mt="auto">
            <Box flex={1}>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                sx={{ fontSize: '0.65rem' }}
              >
                Cost
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
              >
                {FormatUtils.formatCurrency(card.costBasis)}
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                sx={{ fontSize: '0.65rem' }}
              >
                Value
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
              >
                {FormatUtils.formatCurrency(card.currentValue)}
              </Typography>
            </Box>
          </Box>

          {/* Location */}
          {card.location && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ fontSize: '0.65rem' }}
              noWrap
            >
              📍 {card.location}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default CollectionCard;
